import { Add as AddIcon } from '@mui/icons-material';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    TextField,
    Box,
    Typography,
    Chip,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { successToast, errorToast } from '@/common/toast';
import { DataGrid, MarkdownEditor } from '@/components/common';
import { useVideoList } from '@/hooks/videos.hook';
import { useCreateAssignment } from '@/hooks/assignments.hook';

/**
 * 과제 추가 모달 컴포넌트
 * 
 * 동영상 목록에서 하나를 선택하고, 과제 정보를 입력하여 과제를 생성하는 모달입니다.
 * 
 * @param {Object} props
 * @param {boolean} props.open - 모달 열림 상태
 * @param {Function} props.onClose - 모달 닫기 핸들러
 * @param {number} props.courseId - 과정 ID
 * @param {Array<number>} props.existingVideoIds - 이미 과제가 있는 videoId 목록
 * @param {Function} props.onSuccess - 추가 성공 시 콜백
 */
const AssignmentAddModal = ({
    open,
    onClose,
    courseId,
    existingVideoIds = [],
    onSuccess,
}) => {
    const [selectedVideo, setSelectedVideo] = useState(null);

    // 동영상 목록 조회
    const { data: videoData, isLoading: isVideoLoading } = useVideoList({ courseId });
    const videoList = videoData?.data || videoData || [];

    // 과제 생성 mutation
    const createAssignmentMutation = useCreateAssignment();

    // 폼 관리
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            videoId: '',
            title: '',
            description: ``,
            attachedFileUrl: '',
        },
    });

    /**
     * 모달 닫기 핸들러
     */
    const handleClose = useCallback(() => {
        setSelectedVideo(null);
        reset();
        onClose();
    }, [reset, onClose]);

    /**
     * 동영상 선택 핸들러
     */
    const handleVideoSelect = useCallback((video) => {
        setSelectedVideo(video);
        reset({
            videoId: video.videoId,
            title: '',
            description: '',
            attachedFileUrl: '',
        });
    }, [reset, existingVideoIds, selectedVideo]);

    /**
     * 폼 제출 핸들러
     */
    const onSubmit = useCallback(async (data) => {
        if (!selectedVideo) {
            errorToast('동영상을 선택해주세요.');
            return;
        }

        try {
            await createAssignmentMutation.mutateAsync({
                videoId: selectedVideo.videoId,
                title: data.title,
                description: data.description,
                attachedFileUrl: data.attachedFileUrl || null,
            });
            successToast('과제가 추가되었습니다.');
            handleClose();
            onSuccess?.();
        } catch (error) {
            errorToast(`과제 추가에 실패했습니다. - ${error.response?.data?.msg}`);
        }
    }, [selectedVideo, courseId, createAssignmentMutation, handleClose, onSuccess]);

    // 동영상 컬럼 정의
    const videoColumns = useMemo(() => [
        {
            field: 'videoSeq',
            headerName: '순서',
            width: 80,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'videoName',
            headerName: '동영상명',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (
                <Stack direction="row" alignItems="center" sx={{ height: '100%' }} spacing={1}>
                    <Typography variant="body2">
                        {params.row.videoName}
                    </Typography>
                    {existingVideoIds.includes(params.row.videoId) && (
                        <Chip
                            label="과제 존재"
                            size="small"
                            color="default"
                            variant="outlined"
                        />
                    )}
                </Stack>
            ),
        },
        {
            field: 'videoIntro',
            headerName: '소개',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {params.row.videoIntro || '-'}
                </Box>
            ),
        },
    ], [existingVideoIds]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
        >
            <DialogTitle>
                과제 추가
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    {/* 동영상 선택 섹션 */}
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                동영상 선택
                            </Box>
                        </Stack>
                        <DataGrid
                            rows={videoList}
                            columns={videoColumns}
                            loading={isVideoLoading}
                            getRowId={(row) => row.videoId}
                            pagination={false}
                            height={300}
                            onRowClick={(params) => {
                                if (!existingVideoIds.includes(params.row.videoId)) {
                                    handleVideoSelect(params.row);
                                } else {
                                    errorToast('이미 과제가 존재하는 동영상입니다.');
                                }
                            }}
                            isRowSelectable={(params) => !existingVideoIds.includes(params.row.videoId)}
                            sx={{
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: '#f5f5f5',
                                    cursor: 'pointer',
                                },
                                '& .MuiDataGrid-row.Mui-selected': {
                                    backgroundColor: '#e3f2fd',
                                },
                                '& .MuiDataGrid-row:has(.Mui-disabled)': {
                                    opacity: 0.5,
                                    cursor: 'not-allowed',
                                },
                            }}
                            rowSelectionModel={
                                selectedVideo
                                    ? { type: 'include', ids: new Set([selectedVideo.videoId]) }
                                    : { type: 'include', ids: new Set() }
                            }
                            onRowSelectionModelChange={(newSelection) => {
                                const ids = newSelection?.ids ?? new Set();
                                if (ids.size === 0) {
                                    // 선택 취소
                                    setSelectedVideo(null);
                                    reset({
                                        videoId: '',
                                        title: '',
                                        description: '',
                                        attachedFileUrl: '',
                                    });
                                } else {
                                    const selectedId = [...ids][ids.size - 1];
                                    const video = videoList.find((v) => v.videoId === selectedId);
                                    if (video && !existingVideoIds.includes(video.videoId)) {
                                        handleVideoSelect(video);
                                    }
                                }
                            }}
                            checkboxSelection
                            disableRowSelectionOnClick={false}
                            disableRowSelectionExcludeModel
                        />
                        {selectedVideo && (
                            <Box sx={{ mt: 1, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                                <Typography variant="body2" fontWeight={600}>
                                    선택된 동영상: {selectedVideo.videoName}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* 과제 정보 입력 섹션 */}
                    {selectedVideo && (
                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                <Box sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                    과제 정보
                                </Box>
                            </Stack>
                            <Stack spacing={2}>
                                {/* 과제명 */}
                                <Controller
                                    control={control}
                                    name="title"
                                    rules={{
                                        required: '과제명을 입력해주세요.',
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="과제명 *"
                                            fullWidth
                                            variant="outlined"
                                            error={!!errors.title}
                                            helperText={errors.title?.message}
                                        />
                                    )}
                                />

                                {/* 설명 */}
                                <Controller
                                    control={control}
                                    name="description"
                                    rules={{
                                        required: '설명을 입력해주세요.',
                                    }}
                                    render={({ field }) => (
                                        <MarkdownEditor
                                            value={field.value}
                                            onChange={field.onChange}
                                            label="설명 *"
                                            error={!!errors.description}
                                            helperText={errors.description?.message}
                                            height={400}
                                        />
                                    )}
                                />

                                {/* 참고 링크 */}
                                <Controller
                                    control={control}
                                    name="attachedFileUrl"
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="참고 링크"
                                            fullWidth
                                            variant="outlined"
                                            placeholder="https://example.com"
                                            helperText="참고할 링크를 입력해주세요. (선택사항)"
                                        />
                                    )}
                                />
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>취소</Button>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleSubmit(onSubmit)}
                    disabled={!selectedVideo || createAssignmentMutation.isPending}
                >
                    추가
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignmentAddModal;
